import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useContext, useState } from 'react';

import DischargeReasons from '@/components/EpisodeOfCareSection/components/DischargeReasons';
import Phases from '@/components/EpisodeOfCareSection/components/Phases';
import NoteBody from '@/components/NoteBody';
import PersonTreatmentWeekAndDay from '@/components/PersonTreatmentWeekAndDay';
import StartNewPhaseForm from '@/forms/StartNewPhaseForm';
import PersonContext from '@/pages/Person/PersonContext';
import { DualTime, Typography } from '@/ui';
import DataList from '@/ui/components/DataList';
import DataRow from '@/ui/components/DataList/DataRow';

import GraduateOrDischarge from '../GraduateOrDischarge';
import { ProgramCorrectionModal } from '../ProgramCorrectionModal';

import { ActionButtons } from './components';
import type { Props } from './types';

const EpisodeOfCare = ({ editingProgramAndPhase, setEditingProgramAndPhase, title }: Props) => {
  const {
    person: {
      episode_of_care: episodeOfCare,
      id: personId,
      program_qualification: programQualification
    }
  } = useContext(PersonContext);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);

  if (!episodeOfCare) {
    return null;
  }

  return (
    <>
      <div className="flex flex-row">
        {title && (
          <Typography as="h3" variant="body" weight="bold" className="text-lg">
            {title}
          </Typography>
        )}
        {!editingProgramAndPhase && (
          <ActionButtons
            onTransitionClick={() => setEditingProgramAndPhase(true)}
            onCorrectionClick={() => setShowCorrectionModal(true)}
            transitionDisabled={
              !!episodeOfCare.discharged_at || !episodeOfCare.current_active_phase
            }
            correctionDisabled={!!episodeOfCare.discharged_at}
            className="ml-auto"
          />
        )}
      </div>

      <DataList>
        <DataRow label="Program Family">{episodeOfCare.program?.name}</DataRow>

        {episodeOfCare.admitted_at && (
          <DataRow label="Admitted At">
            <PersonTreatmentWeekAndDay />
          </DataRow>
        )}

        <DataRow label="Status">{episodeOfCare.status}</DataRow>

        {episodeOfCare.discharged_at && (
          <>
            <DataRow label={`${episodeOfCare.is_graduated ? 'Graduated' : 'Discharged'} At`}>
              <DualTime dateTime={new Date(episodeOfCare.discharged_at)} />
            </DataRow>

            {episodeOfCare.is_graduated && (
              <DataRow label="Graduation Reason">
                {episodeOfCare.graduation_reason ? episodeOfCare.graduation_reason : 'None'}
              </DataRow>
            )}

            {!episodeOfCare.is_graduated && (
              <DischargeReasons
                dischargeReasons={episodeOfCare.discharge_reasons}
                episodeOfCareId={episodeOfCare.id}
                personId={episodeOfCare.member_id}
              />
            )}
          </>
        )}

        {episodeOfCare.most_recent_note && (
          <DataRow label="Note">
            <NoteBody note={episodeOfCare.most_recent_note} />
          </DataRow>
        )}

        <DataRow label="Phases">
          <Phases phases={episodeOfCare.phases} />
        </DataRow>
      </DataList>

      {editingProgramAndPhase && episodeOfCare.current_active_phase && (
        <div className="rounded-lg border border-blue-300 my-4">
          <div className="rounded-t-lg flex items-center gap-2 px-4 py-3 border-b border-blue-200 bg-blue-50">
            <FontAwesomeIcon className="text-blue-600 mt-0.5 shrink-0" icon={faPlus} />
            <Typography as="h3" variant="body" weight="bold" className="text-lg text-blue-600">
              Transition to a new phase
            </Typography>
          </div>
          <div className="px-4">
            <StartNewPhaseForm
              episodeOfCare={episodeOfCare}
              onCancel={() => setEditingProgramAndPhase(false)}
              onSuccess={() => setEditingProgramAndPhase(false)}
              personId={personId}
            />
          </div>
        </div>
      )}

      {!!programQualification &&
        episodeOfCare.phases.length > 0 &&
        !editingProgramAndPhase &&
        !episodeOfCare.discharged_at && (
          <div className="mt-2">
            <GraduateOrDischarge
              episodeOfCare={episodeOfCare}
              personId={personId}
              setEditingProgramAndPhase={setEditingProgramAndPhase}
            />
          </div>
        )}

      <ProgramCorrectionModal
        onClose={() => setShowCorrectionModal(false)}
        open={showCorrectionModal}
        episodeOfCare={episodeOfCare}
      />
    </>
  );
};

export default EpisodeOfCare;
